// file: src/TowingDashboard/Overview/Components/FloatingSupport.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import HeadsetIcon from "../../../Icons/HeadsetIcon.jsx";
import EmailIcon from "../../../Icons/EmailIcon.jsx";
import PhoneIcon from "../../../Icons/PhoneIcon.jsx";
import MessageIcon from "../../../Icons/MessageIcon.jsx";
import { buildGmailHref, openInNewTab } from "../../../utils/composeLinks.js";

/* Brand colors (no design change) */
const BLUE = "#1271EB";
const BLUE_LIGHT = "#4D95F0";

/* Brand chip styles (replaces bg-brand-500) */
const chipClass =
  "inline-grid place-items-center rounded-full text-white shadow";
const chipStyle = {
  background: `linear-gradient(135deg, ${BLUE}, ${BLUE_LIGHT})`,
  boxShadow: `0 12px 28px ${BLUE}33`,
};

export default function FloatingSupport({
  email = "support@company.com",
  phone = "+1-555-123-4567",
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const myEmail = useMemo(() => {
    try {
      const raw = localStorage.getItem("demo_auth");
      return raw ? JSON.parse(raw)?.email || "" : "";
    } catch {
      return "";
    }
  }, []);

  const gmailSubject = "Towing Portal Support Request";
  const gmailBody = [
    "Hi Towing Portal Support,",
    "",
    "I need help with:",
    "• (briefly describe your issue here)",
    "",
    `My email: ${myEmail || "(your email)"}`,
    "",
    "Thanks,",
  ].join("\n");
  const gmailHref = buildGmailHref({
    to: email,
    subject: gmailSubject,
    body: gmailBody,
  });

  useEffect(() => {
    function onDoc(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  const ui = (
    <div
      ref={wrapRef}
      className="fixed right-4 bottom-4 sm:right-6 sm:bottom-6 z-50 w-12 h-12"
    >
      <AnimatePresence>
        {open && (
          <motion.div
            key="support-card"
            initial={{ opacity: 0, y: 8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 240, damping: 22 }}
            className="
              absolute bottom-14 right-0 w-80 max-w-[90vw]
              rounded-2xl border border-white/70 bg-white/95 backdrop-blur
              shadow-[0_20px_60px_rgba(67,56,202,.10),0_8px_24px_rgba(2,6,23,.06)]
              p-3 origin-bottom-right
            "
            role="dialog"
            aria-label="Contact support"
          >
            <span
              className="absolute -bottom-1 right-4 h-3 w-3 rotate-45 bg-white/90 border-r border-b border-white/70"
              aria-hidden="true"
            />
            <div className="px-1 pb-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-500">
                Contact
              </div>
              <div className="text-slate-800 font-semibold">
                We’re here to help
              </div>
            </div>

            <Item
              icon={
                <span className={`${chipClass} h-8 w-8`} style={chipStyle}>
                  <EmailIcon className="w-4 h-4" />
                </span>
              }
              label="Email"
              value={email}
              href={gmailHref}
              onClick={(e) => openInNewTab(gmailHref, e)}
            />
            <Item
              icon={
                <span className={`${chipClass} h-8 w-8`} style={chipStyle}>
                  <PhoneIcon className="w-4 h-4" />
                </span>
              }
              label="Phone"
              value={phone}
              href={`tel:${phone.replace(/[^+\d]/g, "")}`}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating button uses same blue gradient */}
      <motion.button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={`${chipClass} h-12 w-12`}
        style={chipStyle}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.97 }}
        title="Message / Support"
      >
        <MessageIcon className="w-6 h-6" />
      </motion.button>
    </div>
  );

  if (typeof window !== "undefined") {
    return createPortal(ui, document.body);
  }
  return ui;
}

function Item({ icon, label, value, href, onClick }) {
  return (
    <a
      href={href}
      target={href?.startsWith("http") ? "_blank" : undefined}
      rel={href?.startsWith("http") ? "noopener noreferrer" : undefined}
      onClick={onClick}
      className="flex items-center gap-3 rounded-xl px-2 py-2 hover:bg-slate-50/80 transition"
    >
      {icon}
      <div className="min-w-0">
        <div className="text-[11px] uppercase tracking-wide text-slate-500">
          {label}
        </div>
        <div className="text-sm text-slate-800 truncate">{value}</div>
      </div>
    </a>
  );
}
